import React, { useEffect, useRef, useState } from 'react';
import { enqueueLogo } from './logoLoadQueue';

function LogoImage({ ticker, companyName, size }) {
  const [failed, setFailed] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const containerRef = useRef(null);
  const releaseRef = useRef(null);
  const apiBase = (process.env.REACT_APP_API_BASE_URL || '/api').replace(/\/$/, '');

  useEffect(() => {
    if (!ticker) return undefined;
    let cancelled = false;
    let deferred = false;
    let idleId;
    let timerId;
    let frameId;
    let observer;
    const start = () => {
      if (cancelled) return;
      const logoUrl = `${apiBase}/company-logo/${encodeURIComponent(ticker)}`;
      releaseRef.current = enqueueLogo(() => {
        const release = () => releaseRef.current?.();
        fetch(logoUrl, { headers: { Accept: 'application/json' } })
          .then((response) => (response.ok ? response.json() : null))
          .then((payload) => {
            if (cancelled) {
              release();
              return;
            }
            if (payload?.available) setImageSrc(logoUrl);
            else {
              setFailed(true);
              release();
            }
          })
          .catch(() => {
            if (!cancelled) setFailed(true);
            release();
          });
      });
    };
    const defer = () => {
      if (cancelled || deferred) return;
      deferred = true;
      observer?.disconnect();
      // Let the ticker and stock content paint before requesting any logo.
      frameId = window.requestAnimationFrame(() => {
        if (window.requestIdleCallback) {
          idleId = window.requestIdleCallback(start, { timeout: 1500 });
        } else {
          timerId = window.setTimeout(start, 250);
        }
      });
    };
    if (window.IntersectionObserver) {
      observer = new window.IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) defer();
      });
      observer.observe(containerRef.current);
    } else {
      defer();
    }
    return () => {
      cancelled = true;
      observer?.disconnect();
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timerId);
      if (idleId !== undefined) window.cancelIdleCallback(idleId);
      releaseRef.current?.();
    };
  }, [ticker]);

  return (
    <span
      ref={containerRef}
      aria-hidden="true"
      style={{
        width: size, height: size, minWidth: size, display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center', verticalAlign: 'middle',
        borderRadius: 8, background: '#f1f5f9', color: '#475569',
        overflow: 'hidden', flexShrink: 0, fontSize: size < 32 ? 10 : 12,
        fontWeight: 700, border: '1px solid #e2e8f0', position: 'relative',
      }}
    >
      {!loaded && (ticker.slice(0, 2) || '?')}
      {imageSrc && !failed ? (
        <img
          src={imageSrc}
          alt=""
          title={`${companyName || ticker} logo`}
          width={size}
          height={size}
          loading="eager"
          fetchpriority="low"
          decoding="async"
          onLoad={() => {
            setLoaded(true);
            releaseRef.current?.();
          }}
          onError={() => {
            setLoaded(false);
            setFailed(true);
            releaseRef.current?.();
          }}
          style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'contain', padding: 4, boxSizing: 'border-box', opacity: loaded ? 1 : 0 }}
        />
      ) : null}
    </span>
  );
}

export default function StockLogo({ ticker, companyName, size = 36 }) {
  const symbol = String(ticker || '').trim().toUpperCase();
  // A new symbol gets fresh image state after a previous stock's image failed.
  return <LogoImage key={symbol} ticker={symbol} companyName={companyName} size={size} />;
}
